// Shared axios client + typed API wrappers.
// Auth token is read from localStorage on every request; AuthProvider keeps it in sync.

import axios from "axios";

const BASE = process.env.REACT_APP_API_URL || "";

export const http = axios.create({ baseURL: BASE });

http.interceptors.request.use((config) => {
  const token = localStorage.getItem("mie_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

http.interceptors.response.use(
  (res) => res,
  (err) => {
    // On expired / invalid token, punt the user back to /login.
    if (err?.response?.status === 401 && window.location.pathname !== "/login") {
      localStorage.removeItem("mie_token");
      localStorage.removeItem("mie_user");
      window.location.assign("/login");
    }
    return Promise.reject(err);
  }
);

// ------------------------- Auth -------------------------
export const auth = {
  login: (email, password) =>
    http.post("/api/auth/login", { email, password }).then((r) => r.data),
  me: () => http.get("/api/auth/me").then((r) => r.data),
};

// ------------------------- Agency -------------------------
export const agency = {
  listClients: () => http.get("/api/agency/clients").then((r) => r.data),
  createClient: (body) => http.post("/api/agency/clients", body).then((r) => r.data),
  updateClient: (id, body) => http.put(`/api/agency/clients/${id}`, body).then((r) => r.data),
  portfolio: () => http.get("/api/agency/portfolio").then((r) => r.data),
  alerts: () => http.get("/api/agency/alerts").then((r) => r.data),
  recommendations: (params = {}) =>
    http.get("/api/agency/recommendations", { params }).then((r) => r.data),
  updateRecommendation: (id, body) =>
    http.patch(`/api/agency/recommendations/${id}`, body).then((r) => r.data),
  geoLeaderboard: () => http.get("/api/agency/geo-leaderboard").then((r) => r.data),
  errors: (params = {}) => http.get("/api/agency/errors", { params }).then((r) => r.data),
  errorDetail: (id) => http.get(`/api/agency/errors/${id}`).then((r) => r.data),
  errorStats: () => http.get("/api/agency/errors/stats").then((r) => r.data),
  resolveError: (id, body) => http.patch(`/api/agency/errors/${id}`, body).then((r) => r.data),
  triggerCollect: (clientId) =>
    http.post(`/api/agency/clients/${clientId}/collect`).then((r) => r.data),
  triggerNarrate: (clientId, month = null) =>
    http.post(`/api/agency/clients/${clientId}/narrate`, { month }).then((r) => r.data),
};

// ------------------------- Clients -------------------------
export const clients = {
  overview: (id, month) =>
    http.get(`/api/clients/${id}/overview`, { params: month ? { month } : {} }).then((r) => r.data),
  narrative: (id) => http.get(`/api/clients/${id}/narrative`).then((r) => r.data),
  narrativeHistory: (id) =>
    http.get(`/api/clients/${id}/narrative/history`).then((r) => r.data),
  health: (id) => http.get(`/api/clients/${id}/health`).then((r) => r.data),
  geo: (id) => http.get(`/api/clients/${id}/geo`).then((r) => r.data),
  dataVisibility: (id, days = 90) =>
    http.get(`/api/clients/${id}/data/visibility`, { params: { days } }).then((r) => r.data),
  dataEngagement: (id, days = 90) =>
    http.get(`/api/clients/${id}/data/engagement`, { params: { days } }).then((r) => r.data),
  dataConversion: (id, days = 90) =>
    http.get(`/api/clients/${id}/data/conversion`, { params: { days } }).then((r) => r.data),
  dataSources: (id) => http.get(`/api/clients/${id}/data-sources`).then((r) => r.data),
};

// ------------------------- Onboarding -------------------------
export const onboard = {
  createClient: (body) => http.post("/api/onboard/client", body).then((r) => r.data),
  addDataSource: (body) => http.post("/api/onboard/data-source", body).then((r) => r.data),
  testConnection: (clientId, sourceType) =>
    http.post("/api/onboard/test-connection", { client_id: clientId, source_type: sourceType })
        .then((r) => r.data),
  initialCollect: (clientId) =>
    http.post("/api/onboard/initial-collect", { client_id: clientId }).then((r) => r.data),
  setGeoQueries: (clientId, queries) =>
    http.post("/api/onboard/geo-queries", { client_id: clientId, geo_queries: queries })
        .then((r) => r.data),
  geoTemplates: () => http.get("/api/onboard/templates/geo").then((r) => r.data),
};
